import type { InjectionKey } from "vue";
import ClientSettingsApi, {
   type SupportedLanguage,
   type ClientSettingsResponse,
} from "@/data/api-layer/clientSettingsApi";
import type { Dayjs } from "dayjs";
import { convertToUTC } from "@/js/helpers/dates.utils";

// FE naming for the '/client-settings' data
export declare interface ClientSettings {
   // Platform experience
   externalContributions: { isActive: boolean };
   traitReports: { isActive: boolean };
   userDocuments: { isActive: boolean };
   intercom: {
      isActive: boolean;
      appId: string | null;
   };
   cookiesDateChanged: Dayjs | null;
   // Registration and login
   portalInvitationOnlyRegistration: boolean;
   registrationCodeRequired: boolean;
   signup: { isActive: boolean };
   requireCoreProfileOnLogin: boolean; // should we show the core profile flow after the user sets their password?
   googleSignInEnabled: boolean;
   // Languages
   defaultLocale: string;
   supportedLocales: SupportedLanguage[]; // any supported locale other than the default locale
   // Analytics
   metaPixelAnalytics: { isActive: boolean };
   plausibleAnalytics: { isActive: boolean };
   googleAnalytics: { isActive: boolean; code: string };
}

// this has to be exported to be used in routes.ts (you cant inject in routes)
export let clientSettings: ClientSettings = null;

export const mapClientSettingsApi = (data: ClientSettingsResponse): ClientSettings => {
   return {
      // Platform experience
      intercom: { isActive: data.INTERCOM_ENABLED, appId: data.INTERCOM_APP_ID },
      externalContributions: { isActive: data.EXTERNAL_CONTRIBUTIONS_ENABLED },
      traitReports: { isActive: data.TRAIT_REPORTS_ENABLED },
      userDocuments: { isActive: data.USER_DOCUMENTS_ENABLED },
      cookiesDateChanged: convertToUTC(data.COOKIES_DATE_CHANGED),
      // Registration and login
      portalInvitationOnlyRegistration: data.PORTAL_INVITATION_ONLY_REGISTRATION,
      registrationCodeRequired: data.REGISTRATION_CODE_REQUIRED,
      signup: { isActive: data.SIGNUP_ENABLED },
      requireCoreProfileOnLogin: data.REQUIRE_CORE_PROFILE_ON_LOGIN,
      googleSignInEnabled: data.GOOGLE_SIGN_IN_ENABLED,
      // Languages
      defaultLocale: data.DEFAULT_LANGUAGE,
      supportedLocales: data.SUPPORTED_LANGUAGES,
      // Analytics
      plausibleAnalytics: { isActive: data.PLAUSIBLE_ANALYTICS_ENABLED },
      metaPixelAnalytics: {
         isActive: data.META_PIXEL_ANALYTICS_ENABLED,
      },
      googleAnalytics: {
         isActive: data.GOOGLE_ANALYTICS_ENABLED,
         code: data.GOOGLE_ANALYTICS_CODE,
      },
   };
};

// exported to call in main.ts
export async function fetchClientSettings(): Promise<ClientSettings> {
   try {
      // do not apply the snake-to-camel conversion in this case by setting the ignoreCamelConversion to true
      const response = await ClientSettingsApi.get();
      clientSettings = mapClientSettingsApi(response);
      return clientSettings;
   } catch (e) {
      console.error(`❌ Network error - Could not fetch Client Settings in main: ${e}`);
   }
}

export const useClientSettingsKey = Symbol() as InjectionKey<ClientSettings>;

export const vueUseClientSettings = {
   install: (app) => {
      app.provide(useClientSettingsKey, {
         ...clientSettings,
      } as ClientSettings);
   },
};
